import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, Show } from "@clerk/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MessageCircle, X, Send, Sparkles, UserRound } from "lucide-react";
import { makeApi, BACKEND_URL, previewHeaders } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";

async function streamAI(getToken, text, onDelta) {
  const token = await getToken();
  const resp = await fetch(`${BACKEND_URL}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...previewHeaders() },
    body: JSON.stringify({ message: text }),
  });
  if (resp.status === 402) return "limit";
  if (!resp.ok) throw new Error("chat failed");
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return "ok";
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => {
      try {
        const o = JSON.parse(p.replace(/^data:\s*/, ""));
        if (o.delta) onDelta(o.delta);
      } catch {}
    });
  }
}

function Bubble({ m }) {
  const mine = m.role === "user" || m.sender === "client";
  const text = m.content ?? m.body;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-3 py-2 text-sm ${mine ? "bg-[#0055FF] text-white whitespace-pre-wrap" : "bg-[#141414] border border-[#262626] ai-md"}`}>
        {!text ? "…" : mine || m.sender ? text : <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>}
      </div>
    </div>
  );
}

function Panel({ onClose, adminMode }) {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [tab, setTab] = useState("ai");
  const [ai, setAi] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    api.get("/chat/history").then(({ data }) => setAi(data.messages.slice(-20))).catch(() => {});
  }, [api]);
  useEffect(() => {
    if (tab === "admin") api.get("/messages").then(({ data }) => setMsgs(data.messages)).catch(() => {});
  }, [tab, api]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [ai, msgs, tab]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    try {
      if (tab === "admin") {
        await api.post("/messages", { body: text });
        const { data } = await api.get("/messages");
        setMsgs(data.messages);
      } else {
        setAi((a) => [...a, { role: "user", content: text }, { role: "assistant", content: "" }]);
        const r = await streamAI(getToken, text, (d) => setAi((a) => {
          const c = [...a];
          c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d };
          return c;
        }));
        if (r === "limit") {
          setAi((a) => a.slice(0, -2));
          setLimit(true);
        }
      }
    } catch {
      setInput(text);
    } finally {
      setBusy(false);
    }
  };

  const list = tab === "ai" ? ai : msgs;
  return (
    <div className="fixed z-50 inset-x-2 bottom-[calc(76px+env(safe-area-inset-bottom))] md:inset-x-auto md:right-6 md:bottom-24 md:w-[400px] h-[70dvh] md:h-[560px] bg-[#0A0A0A] brutal-border shadow-2xl flex flex-col" data-testid="chat-widget-panel">
      <div className="flex items-center border-b border-[#262626]">
        <button onClick={() => setTab("ai")} data-testid="chat-widget-tab-ai" className={`flex-1 min-h-[52px] font-mono-label text-[11px] inline-flex items-center justify-center gap-2 ${tab === "ai" ? "text-[#FFD600] border-b-2 border-[#FFD600]" : "text-muted-foreground"}`}>
          <Sparkles size={14} /> Ask AI
        </button>
        {!adminMode && (
          <button onClick={() => setTab("admin")} data-testid="chat-widget-tab-admin" className={`flex-1 min-h-[52px] font-mono-label text-[11px] inline-flex items-center justify-center gap-2 ${tab === "admin" ? "text-[#FFD600] border-b-2 border-[#FFD600]" : "text-muted-foreground"}`}>
            <UserRound size={14} /> Message Admin
          </button>
        )}
        <button onClick={onClose} aria-label="Close chat" data-testid="chat-widget-close" className="w-12 min-h-[52px] flex items-center justify-center text-muted-foreground hover:text-white"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chat-widget-messages">
        {list.length === 0 && (
          <p className="text-sm text-muted-foreground p-2">
            {tab === "ai" ? "Ask about FMCSA rules, IFTA, IRP, ELD, CDL endorsements, insurance minimums and more." : "Send a message to the CarrierLaunch team (Ship@mbstransit.com). We'll reply here."}
          </p>
        )}
        {list.map((m, i) => <Bubble key={i} m={m} />)}
        {tab === "ai" && limit && (
          <div className="text-sm p-3 border border-[#FFD600]/50" data-testid="chat-widget-limit">
            You've used your free AI questions. <Link to="/pricing" className="underline text-[#FFD600]" onClick={onClose}>Pick a Plan</Link> to keep asking.
          </div>
        )}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#262626] p-2 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-widget-input"
          placeholder={tab === "ai" ? "Ask a compliance question…" : "Write a message…"}
          className="flex-1 min-w-0 min-h-[44px] bg-[#141414] border border-[#262626] px-3 text-base sm:text-sm focus:outline-none focus:border-white" />
        <button type="submit" disabled={busy} data-testid="chat-widget-send" aria-label="Send" className="cta-primary min-h-[44px] w-12 inline-flex items-center justify-center disabled:opacity-50"><Send size={16} /></button>
      </form>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const { actingAsAdmin, isAdmin } = useRole();
  const { pathname } = useLocation();
  if (isAdmin === null || pathname.startsWith("/admin/messages") || pathname === "/chat" || pathname === "/community" || pathname === "/admin/plans" || pathname === "/subscription-expired") return null;
  return (
    <Show when="signed-in">
      {open && <Panel onClose={() => setOpen(false)} adminMode={actingAsAdmin} />}
      <button onClick={() => setOpen((o) => !o)} data-testid="chat-widget-button" aria-label="Open chat"
        className="fixed z-50 right-3 md:right-6 bottom-[calc(76px+env(safe-area-inset-bottom))] md:bottom-6 w-14 h-14 rounded-full bg-[#FFD600] text-[#0A0A0A] shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        style={open ? { display: "none" } : undefined}>
        <MessageCircle size={24} />
      </button>
    </Show>
  );
}
